import { useState, useEffect, useMemo } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth.store";
import { companyFeaturesApi } from "@/services/api";
import {
  LayoutDashboard,
  Building2,
  Users,
  LogOut,
  Wallet,
  ArrowLeftRight,
  FileText,
  ShieldCheck,
  Mail,
  Bot,
  Landmark,
  UsersRound,
  Package,
  TrendingUp,
  Calendar,
  Clock,
  Link2,
  Coins,
  Activity,
  PiggyBank,
  ChevronDown,
  Key,
  X,
  Menu,
  ArrowUpRight,
  Calculator,
  DollarSign,
  Shield,
  Send,
  CheckCircle,
} from "lucide-react";

interface NavItem {
  label: string;
  href: string;
  icon: React.ReactNode;
  requiredFeature?: string; // Feature necessária para exibir este item
}

const adminNavItems: NavItem[] = [
  { label: "Dashboard", href: "/admin/dashboard", icon: <LayoutDashboard className="w-4 h-4" /> },
  { label: "Empresas", href: "/admin/companies", icon: <Building2 className="w-4 h-4" /> },
  { label: "Operações", href: "/admin/operations", icon: <Activity className="w-4 h-4" /> },
  { label: "Resgates", href: "/admin/withdrawals", icon: <ArrowUpRight className="w-4 h-4" /> },
  { label: "Aplicações", href: "/admin/applications", icon: <PiggyBank className="w-4 h-4" /> },
  { label: "Trades USD", href: "/admin/usdc-trades", icon: <ArrowLeftRight className="w-4 h-4" /> },
  { label: "Provedores USD", href: "/admin/providers", icon: <DollarSign className="w-4 h-4" /> },
  { label: "Proteção Cambial", href: "/admin/hedges", icon: <Shield className="w-4 h-4" /> },
  { label: "Aprovações Pgto", href: "/admin/payments", icon: <CheckCircle className="w-4 h-4" /> },
  { label: "Carteiras", href: "/admin/wallets", icon: <Wallet className="w-4 h-4" /> },
  { label: "Usuários", href: "/admin/users", icon: <Users className="w-4 h-4" /> },
  { label: "Usuários Internos", href: "/admin/internal-users", icon: <ShieldCheck className="w-4 h-4" /> },
  { label: "Produtos", href: "/admin/products", icon: <Package className="w-4 h-4" /> },
  { label: "Posições", href: "/admin/positions", icon: <TrendingUp className="w-4 h-4" /> },
];

const adminSettingsItems: NavItem[] = [
  { label: "Email (SES)", href: "/admin/settings/email", icon: <Mail className="w-4 h-4" /> },
  { label: "IA (Anthropic)", href: "/admin/settings/ai", icon: <Bot className="w-4 h-4" /> },
  { label: "Bancos", href: "/admin/bank-gateway", icon: <Landmark className="w-4 h-4" /> },
  { label: "Liquidez USD", href: "/admin/settings/liquidity-provider", icon: <ArrowLeftRight className="w-4 h-4" /> },
  { label: "Pagamentos", href: "/admin/settings/payment-gateway", icon: <Send className="w-4 h-4" /> },
  { label: "Blockchain", href: "/admin/settings/blockchain", icon: <Link2 className="w-4 h-4" /> },
  { label: "Stablecoin", href: "/admin/settings/stablecoin", icon: <Coins className="w-4 h-4" /> },
  { label: "CDI", href: "/admin/settings/cdi", icon: <TrendingUp className="w-4 h-4" /> },
  { label: "Simulação CDI", href: "/admin/settings/cdi-simulation", icon: <Calculator className="w-4 h-4" /> },
  { label: "Feriados", href: "/admin/settings/holidays", icon: <Calendar className="w-4 h-4" /> },
  { label: "Jobs", href: "/admin/jobs", icon: <Clock className="w-4 h-4" /> },
  { label: "Iniciador", href: "/admin/settings/iniciador", icon: <Link2 className="w-4 h-4" /> },
];

const companyNavItems: NavItem[] = [
  { label: "Dashboard", href: "/dashboard", icon: <LayoutDashboard className="w-4 h-4" /> },
  { label: "BRLD", href: "/brld", icon: <Coins className="w-4 h-4" /> },
  { label: "USDC", href: "/usdc", icon: <DollarSign className="w-4 h-4" /> },
  { label: "Aplicar", href: "/aplicar", icon: <PiggyBank className="w-4 h-4" /> },
  { label: "Converter", href: "/converter", icon: <ArrowLeftRight className="w-4 h-4" /> },
  { label: "Proteção Cambial", href: "/protecao-cambial", icon: <Shield className="w-4 h-4" />, requiredFeature: "IMPORT_HEDGE" },
  { label: "Pagamentos", href: "/pagamentos", icon: <Send className="w-4 h-4" />, requiredFeature: "INTERNATIONAL_PAYMENTS" },
  { label: "Carteira", href: "/wallet", icon: <Wallet className="w-4 h-4" /> },
  { label: "Extrato", href: "/statements", icon: <FileText className="w-4 h-4" /> },
];

// Itens que só aparecem para ADMIN da empresa
const companyAdminItems: NavItem[] = [
  { label: "Equipe", href: "/company/users", icon: <UsersRound className="w-4 h-4" /> },
  { label: "Contas Bancárias", href: "/bank-accounts", icon: <Landmark className="w-4 h-4" /> },
  { label: "Open Finance", href: "/open-finance", icon: <Link2 className="w-4 h-4" /> },
  { label: "API Keys", href: "/api-keys", icon: <Key className="w-4 h-4" /> },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const [settingsExpanded, setSettingsExpanded] = useState(false);

  const isInternal = user?.type === "internal";
  const isCompanyAdmin = user?.type === "company" && user?.role === "ADMIN";

  // Buscar features habilitadas para a empresa (apenas para usuários company)
  const { data: enabledFeatures } = useQuery({
    queryKey: ["company-features"],
    queryFn: () => companyFeaturesApi.getEnabled(),
    enabled: !isInternal, // Só busca se for usuário company
    staleTime: 5 * 60 * 1000, // 5 minutos de cache
  });

  // Filtrar itens do menu baseado nas features habilitadas
  const filteredCompanyNavItems = useMemo(() => {
    if (!enabledFeatures) {
      // Enquanto carrega, mostra apenas itens sem requiredFeature
      return companyNavItems.filter(item => !item.requiredFeature);
    }
    return companyNavItems.filter(item => {
      if (!item.requiredFeature) return true;
      return enabledFeatures.includes(item.requiredFeature);
    });
  }, [enabledFeatures]);

  // Close sidebar on route change (mobile)
  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed lg:static inset-y-0 left-0 z-50 w-60 bg-[#0a0a0a] flex flex-col transform transition-transform duration-300 ease-in-out lg:transform-none",
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        {/* Logo */}
        <div className="px-5 py-6 border-b border-neutral-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center p-1.5">
              <img src="/images/logo-brld.svg" alt="BRLD" className="w-full h-full" />
            </div>
            <div>
              <h1 className="text-base font-semibold text-white tracking-tight">BRLD</h1>
              <p className="text-[10px] text-neutral-500 uppercase tracking-wider">
                {isInternal ? "Admin" : "Empresa"}
              </p>
            </div>
          </div>
          {/* Close button - mobile only */}
          <button
            onClick={onClose}
            className="lg:hidden p-2 text-neutral-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {/* Admin Internal Navigation */}
          {isInternal && (
            <>
              {adminNavItems.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150",
                      isActive
                        ? "bg-white text-black"
                        : "text-neutral-400 hover:bg-neutral-800/50 hover:text-white"
                    )
                  }
                >
                  {item.icon}
                  {item.label}
                </NavLink>
              ))}

              {/* Settings section (Admin only) - Collapsible */}
              <div className="pt-4">
                <button
                  onClick={() => setSettingsExpanded(!settingsExpanded)}
                  className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-semibold text-neutral-500 uppercase tracking-wider hover:text-neutral-400 transition-colors"
                >
                  <span>Configurações</span>
                  <ChevronDown
                    className={cn(
                      "w-3.5 h-3.5 transition-transform duration-200",
                      settingsExpanded && "rotate-180"
                    )}
                  />
                </button>
              </div>
              <div
                className={cn(
                  "overflow-hidden transition-all duration-200",
                  settingsExpanded ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
                )}
              >
                {adminSettingsItems.map((item) => (
                  <NavLink
                    key={item.href}
                    to={item.href}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150",
                        isActive
                          ? "bg-white text-black"
                          : "text-neutral-400 hover:bg-neutral-800/50 hover:text-white"
                      )
                    }
                  >
                    {item.icon}
                    {item.label}
                  </NavLink>
                ))}
              </div>
            </>
          )}

          {/* Company Navigation */}
          {!isInternal && (
            <>
              {filteredCompanyNavItems.map((item) => (
                <NavLink
                  key={item.href}
                  to={item.href}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150",
                      isActive
                        ? "bg-white text-black"
                        : "text-neutral-400 hover:bg-neutral-800/50 hover:text-white"
                    )
                  }
                >
                  {item.icon}
                  {item.label}
                </NavLink>
              ))}

              {/* Admin section (Company ADMIN only) */}
              {isCompanyAdmin && (
                <>
                  <div className="pt-4 pb-2">
                    <p className="px-3 text-[10px] font-semibold text-neutral-500 uppercase tracking-wider">
                      Administração
                    </p>
                  </div>
                  {companyAdminItems.map((item) => (
                    <NavLink
                      key={item.href}
                      to={item.href}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150",
                          isActive
                            ? "bg-white text-black"
                            : "text-neutral-400 hover:bg-neutral-800/50 hover:text-white"
                        )
                      }
                    >
                      {item.icon}
                      {item.label}
                    </NavLink>
                  ))}
                </>
              )}

            </>
          )}
        </nav>

        {/* User Info & Logout */}
        <div className="px-3 py-4 border-t border-neutral-800">
          <div className="px-3 mb-3">
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-neutral-500 truncate">{user?.email}</p>
            {user?.companyName && (
              <p className="text-xs text-neutral-600 truncate mt-0.5">
                {user.companyName}
              </p>
            )}
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-neutral-400 hover:bg-neutral-800/50 hover:text-white transition-all duration-150"
          >
            <LogOut className="w-4 h-4" />
            Sair
          </button>
        </div>
      </aside>
    </>
  );
}

// Mobile menu button component
export function MobileMenuButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="lg:hidden p-2 text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 rounded-lg transition-colors"
      aria-label="Abrir menu"
    >
      <Menu className="w-5 h-5" />
    </button>
  );
}
